import { supabase } from '@/lib/supabase'

export type SuggestionStatus = 'pending' | 'published' | 'approved' | 'rejected'

export type Suggestion = {
  id: string
  title: string
  content: string
  author_id: string
  author_name: string
  author_avatar_url: string | null
  status: SuggestionStatus
  review_note?: string | null
  review_note_visibility?: 'author' | 'public' | null
  author_private_note?: string | null
  approval_threshold_pct?: number
  created_at?: string
  updated_at?: string
}

export type SuggestionVoteRow = {
  suggestion_id: string
  user_id: string
  vote: 'up' | 'down'
}

export type RegisterVoteResult = {
  suggestion_id: string
  votes_up: number
  votes_down: number
  eligible_total: number
  target_min: number
  status: SuggestionStatus
  user_vote: 'up' | 'down' | null
}

export async function listAllSuggestions(): Promise<Suggestion[]> {
  const { data, error } = await supabase
    .from('feature_suggestions')
    .select(
      [
        'id',
        'title',
        'content',
        'author_id',
        'author_name',
        'author_avatar_url',
        'status',
        'review_note',
        'review_note_visibility',
        'author_private_note',
        'approval_threshold_pct',
        'created_at',
        'updated_at',
      ].join(','),
    )
    .order('created_at', { ascending: false })

  if (error) throw error
  const base = (data as any[]).map((row) => normalizeSuggestion(row))

  // Reflete sempre o nome/avatar atuais do perfil
  const authorIds = Array.from(
    new Set(base.map((s) => s.author_id).filter((v) => !!v)),
  ) as string[]
  if (authorIds.length === 0) return base

  try {
    const { data: profiles, error: profErr } = await supabase
      .from('profiles')
      .select('id, full_name, avatar_url')
      .in('id', authorIds)
    if (!profErr && Array.isArray(profiles)) {
      const byId = new Map(
        (profiles as any[]).map((p) => [String(p.id), p]),
      )
      return base.map((s) => {
        const p = byId.get(s.author_id)
        if (!p) return s
        return {
          ...s,
          author_name: (p.full_name as string) || s.author_name,
          author_avatar_url:
            (p.avatar_url as string | null) ?? s.author_avatar_url,
        }
      })
    }
  } catch {
    // Em caso de erro de RLS ou rede, usamos apenas o snapshot
  }

  return base
}

export async function listSuggestionVotes(): Promise<SuggestionVoteRow[]> {
  const { data, error } = await supabase
    .from('feature_suggestion_votes')
    .select('suggestion_id,user_id,vote')

  if (error) throw error
  return ((data || []) as any[]).map((row) => ({
    suggestion_id: String(row.suggestion_id),
    user_id: String(row.user_id),
    vote: (String(row.vote).toLowerCase() === 'down' ? 'down' : 'up') as 'up' | 'down',
  }))
}

export async function fetchEligibleVotersCount(): Promise<number> {
  const { data, error } = await supabase.rpc('feature_suggestions_total_voters' as any)
  if (error) throw error
  const v = typeof data === 'number' ? data : Number(data || 0)
  if (!Number.isFinite(v) || v < 0) return 0
  return v
}

export async function createSuggestion(payload: {
  title: string
  content: string
  authorName: string
  authorAvatarUrl?: string | null
}): Promise<{ id: string }> {
  const body: any = {
    title: payload.title,
    content: payload.content,
    author_name: payload.authorName,
    author_avatar_url: payload.authorAvatarUrl ?? null,
  }
  const { data, error } = await supabase
    .from('feature_suggestions')
    .insert(body)
    .select('id')
    .single()

  if (error) throw error
  return { id: String((data as any).id) }
}

export async function updateSuggestion(
  id: string,
  patch: { title?: string; content?: string; approvalThresholdPct?: number },
): Promise<void> {
  const body: any = {}
  if (typeof patch.title === 'string') body.title = patch.title
  if (typeof patch.content === 'string') body.content = patch.content
  if (typeof patch.approvalThresholdPct === 'number' && Number.isFinite(patch.approvalThresholdPct)) {
    const raw = Math.round(patch.approvalThresholdPct)
    body.approval_threshold_pct = Math.min(100, Math.max(1, raw))
  }
  if (Object.keys(body).length === 0) return

  const { error } = await supabase
    .from('feature_suggestions')
    .update(body)
    .eq('id', id)
  if (error) throw error
}

export async function reviewSuggestion(
  id: string,
  action: 'approve' | 'reject',
  opts: {
    note?: string | null
    visibility?: 'author' | 'public'
    authorPrivateNote?: string | null
    thresholdPct?: number | null
  },
): Promise<void> {
  const body: any = {
    status: action === 'approve' ? 'published' : 'rejected',
  }
  if (typeof opts.note === 'string') body.review_note = opts.note.trim() || null
  if (opts.visibility) body.review_note_visibility = opts.visibility

  if (action === 'approve') {
    if (typeof opts.authorPrivateNote === 'string') {
      body.author_private_note = opts.authorPrivateNote.trim() || null
    }
    if (typeof opts.thresholdPct === 'number' && Number.isFinite(opts.thresholdPct)) {
      const raw = Math.round(opts.thresholdPct)
      const clamped = Math.min(100, Math.max(1, raw))
      body.approval_threshold_pct = clamped
    }
  } else {
    // Em negações, limpamos qualquer mensagem privada anterior
    body.author_private_note = null
  }

  const { error } = await supabase
    .from('feature_suggestions')
    .update(body)
    .eq('id', id)
  if (error) throw error
}

export async function deleteSuggestion(id: string): Promise<void> {
  const { error } = await supabase
    .from('feature_suggestions')
    .delete()
    .eq('id', id)
  if (error) throw error
}

export async function registerSuggestionVote(
  suggestionId: string,
  vote: 'up' | 'down',
): Promise<RegisterVoteResult> {
  const auth = await supabase.auth.getUser()
  const userId = auth.data.user?.id
  if (!userId) throw new Error('Usuário não autenticado para votar')

  // 1) Upsert do voto na tabela de votos (RLS garante user_id = auth.uid())
  const { error: upsertError } = await supabase
    .from('feature_suggestion_votes')
    .upsert(
      { suggestion_id: suggestionId, user_id: userId, vote } as any,
      { onConflict: 'suggestion_id,user_id' },
    )
  if (upsertError) throw upsertError

  // 2) Recarrega todos os votos dessa sugestão e agrega
  const { data: aggRows, error: aggError } = await supabase
    .from('feature_suggestion_votes')
    .select('suggestion_id,user_id,vote')
    .eq('suggestion_id', suggestionId)
  if (aggError) throw aggError
  const rows = (aggRows || []) as any[]
  const votesUp = rows.filter((r) => String(r.vote).toLowerCase() === 'up').length
  const votesDown = rows.filter((r) => String(r.vote).toLowerCase() === 'down').length

  // 3) Total elegível e alvo mínimo (SSOT via função SQL)
  const eligible_total = await fetchEligibleVotersCount()
  const target_min = eligible_total > 0 ? Math.ceil(eligible_total * 0.51) : 0

  // 4) Status atual da sugestão
  const { data: statusRow, error: statusError } = await supabase
    .from('feature_suggestions')
    .select('status')
    .eq('id', suggestionId)
    .maybeSingle()
  if (statusError) throw statusError
  const statusValue = normalizeStatus((statusRow as any)?.status)

  const lastVoteRow = rows.find((r) => String(r.user_id) === String(userId))
  const userVote =
    String(lastVoteRow?.vote || '').toLowerCase() === 'down' ? 'down' : 'up'

  return {
    suggestion_id: suggestionId,
    votes_up: votesUp,
    votes_down: votesDown,
    eligible_total,
    target_min,
    status: statusValue,
    user_vote: userVote,
  }
}

export async function removeSuggestionVote(
  suggestionId: string,
): Promise<RegisterVoteResult> {
  const auth = await supabase.auth.getUser()
  const userId = auth.data.user?.id
  if (!userId) throw new Error('Usuário não autenticado para votar')

  const { error: deleteError } = await supabase
    .from('feature_suggestion_votes')
    .delete()
    .match({ suggestion_id: suggestionId, user_id: userId } as any)

  if (deleteError) throw deleteError

  const { data: aggRows, error: aggError } = await supabase
    .from('feature_suggestion_votes')
    .select('suggestion_id,user_id,vote')
    .eq('suggestion_id', suggestionId)
  if (aggError) throw aggError
  const rows = (aggRows || []) as any[]
  const votesUp = rows.filter((r) => String(r.vote).toLowerCase() === 'up').length
  const votesDown = rows.filter((r) => String(r.vote).toLowerCase() === 'down').length

  const eligible_total = await fetchEligibleVotersCount()
  const target_min = eligible_total > 0 ? Math.ceil(eligible_total * 0.51) : 0

  const { data: statusRow, error: statusError } = await supabase
    .from('feature_suggestions')
    .select('status')
    .eq('id', suggestionId)
    .maybeSingle()
  if (statusError) throw statusError
  const statusValue = normalizeStatus((statusRow as any)?.status)

  return {
    suggestion_id: suggestionId,
    votes_up: votesUp,
    votes_down: votesDown,
    eligible_total,
    target_min,
    status: statusValue,
    user_vote: null,
  }
}

function normalizeSuggestion(row: any): Suggestion {
  const thresholdRaw = Number(row.approval_threshold_pct ?? 51)
  const thresholdPct = Number.isFinite(thresholdRaw)
    ? Math.min(100, Math.max(1, Math.round(thresholdRaw)))
    : 51

  return {
    id: String(row.id),
    title: String(row.title || ''),
    content: String(row.content || ''),
    author_id: String(row.author_id),
    author_name: String(row.author_name || ''),
    author_avatar_url: row.author_avatar_url || null,
    status: normalizeStatus(row.status),
    review_note: row.review_note ?? null,
    review_note_visibility: (row.review_note_visibility as any) ?? null,
    author_private_note: row.author_private_note ?? null,
    approval_threshold_pct: thresholdPct,
    created_at: row.created_at ? String(row.created_at) : undefined,
    updated_at: row.updated_at ? String(row.updated_at) : undefined,
  }
}

function normalizeStatus(v: any): SuggestionStatus {
  const s = String(v || '').toLowerCase()
  if (s === 'pending' || s === 'published' || s === 'approved' || s === 'rejected') return s
  return 'pending'
}
